import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../supabaseClient';
import CompareOverlay from './CompareOverlay';
import CompareCards, { LineList, Pill } from './CompareCards';
import { requirementsTotal } from '../../utils/applicationReviews';

const GREEN = '#1D9E75';
const RED   = '#E24B4A';
const AMBER = '#F5A623';

// TIER GATE -- intentionally absent. Union billing doesn't exist yet, so every union gets this.
// When a union plan tier is built, gate it here the same way EmployerCandidateCompare does
// (employerCanCompare / COMPARE_TIERS): show the upgrade prompt in place of <CompareCards>.

const STATUS_COLOR = { pending: 'rgba(255,255,255,0.6)', reviewing: AMBER, accepted: GREEN, rejected: RED };

export default function UnionApplicantCompare({ applicants, onRemove, onClear, onClose }) {
  const { t } = useTranslation();
  const [details, setDetails] = useState({});
  const [loading, setLoading] = useState(true);

  const key = applicants.map(a => a.id).sort().join(',');
  useEffect(() => {
    if (applicants.length === 0) { setLoading(false); return; }
    let cancelled = false;
    (async () => {
      setLoading(true);
      const workerIds = [...new Set(applicants.map(a => a.worker_id))];
      const apprIds = [...new Set(applicants.map(a => a.apprenticeship_id))];
      const [trades, experience, apprs, examLinks] = await Promise.all([
        supabase.from('worker_trades').select('worker_id, trade, years_experience').in('worker_id', workerIds),
        supabase.from('worker_experience').select('worker_id, role, company, start_year, end_year, current').in('worker_id', workerIds).order('start_year', { ascending: false }),
        supabase.from('apprenticeships').select('id, title, trade, requirements').in('id', apprIds),
        supabase.from('apprenticeship_exam_requirements').select('apprenticeship_id, union_exams(name)').in('apprenticeship_id', apprIds),
      ]);
      if (cancelled) return;
      const apprMap = Object.fromEntries((apprs.data || []).map(a => [a.id, a]));
      const next = {};
      applicants.forEach(a => {
        const appr = apprMap[a.apprenticeship_id];
        const apprTrade = (appr?.trade || '').toLowerCase();
        const workerTrades = (trades.data || []).filter(r => r.worker_id === a.worker_id);
        next[a.id] = {
          appr,
          trade: workerTrades.find(r => (r.trade || '').toLowerCase() === apprTrade) || workerTrades[0] || null,
          experience: (experience.data || []).filter(r => r.worker_id === a.worker_id),
          exams: (examLinks.data || []).filter(l => l.apprenticeship_id === a.apprenticeship_id && l.union_exams).map(l => l.union_exams.name),
        };
      });
      setDetails(next);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps

  const items = applicants.map(a => ({
    id: a.id,
    title: `${a.worker?.first_name || ''} ${a.worker?.last_name || ''}`.trim() || t('compare.unnamed'),
    subtitle: a.apprenticeship?.title || t('union.dashboard.candidates.apprenticeshipFallback'),
    applicant: a,
  }));
  const d = item => details[item.id] || {};
  // Exam result, requirements met and staff notes come from the staff-only review table, loaded
  // with the applicant (candidate.review; all null when nothing has been recorded). The posting
  // whose requirements count as the "M" is that same loaded candidate.apprenticeship -- the one
  // the Candidates tab's stepper uses -- so the two totals can't disagree.
  const rv = item => item.applicant.review || {};
  const reqsOf = item => (item.applicant.apprenticeship?.requirements ?? d(item).appr?.requirements) || [];

  const rows = [
    {
      key: 'status', label: t('compare.union.rows.status'),
      render: item => {
        const s = item.applicant.status || 'pending';
        const labelKey = { pending: 'statusPending', reviewing: 'statusReviewing', accepted: 'statusAccepted', rejected: 'statusRejected' }[s];
        return <Pill color={STATUS_COLOR[s] || AMBER}>{labelKey ? t(`union.dashboard.candidates.${labelKey}`) : s}</Pill>;
      },
    },
    {
      key: 'trade', label: t('compare.union.rows.tradeExperience'),
      render: item => {
        const tr = d(item).trade;
        const exp = d(item).experience || [];
        if (!tr && exp.length === 0) return null;
        return (
          <LineList values={[
            tr && [tr.trade, tr.years_experience].filter(Boolean).join(' · '),
            ...exp.slice(0, 3).map(e => `${[e.role, e.company].filter(Boolean).join(t('compare.atSeparator'))} (${e.start_year || '?'}–${e.current ? t('compare.present') : (e.end_year || '?')})`),
          ].filter(Boolean)} />
        );
      },
    },
    {
      // Requirements met is a count the union records by hand; the posting's own list is shown
      // underneath as reference, since which specific ones were met isn't stored.
      key: 'requirements', label: t('compare.union.rows.requirementsMet'),
      render: item => {
        const reqs = reqsOf(item);
        const total = requirementsTotal({ requirements: reqs });
        const met = rv(item).requirements_met;
        if (met === null || met === undefined) {
          return reqs.length ? <span style={{ color: 'rgba(255,255,255,0.45)' }}>{t('compare.notRecorded')}</span> : null;
        }
        const all = total > 0 && met >= total;
        return (
          <div>
            <Pill color={all ? GREEN : AMBER}>
              {total ? t('compare.union.metOfTotal', { met, total }) : t('compare.union.metCount', { count: met })}
            </Pill>
            {reqs.length > 0 && (
              <div style={{ marginTop: '6px', fontSize: '0.76rem', color: 'rgba(255,255,255,0.5)' }}>
                <LineList values={reqs.map(r => `• ${r}`)} />
              </div>
            )}
          </div>
        );
      },
    },
    {
      key: 'exam', label: t('compare.union.rows.examResult'),
      render: item => {
        const r = rv(item).exam_result;
        const exams = d(item).exams || [];
        return (
          <div>
            {r === 'passed' ? <Pill color={GREEN}>{t('compare.union.examPassed')}</Pill>
              : r === 'failed' ? <Pill color={RED}>{t('compare.union.examFailed')}</Pill>
              : <span style={{ color: 'rgba(255,255,255,0.45)' }}>{t('compare.notRecorded')}</span>}
            {exams.length > 0 && (
              <div style={{ marginTop: '6px', fontSize: '0.76rem', color: 'rgba(255,255,255,0.5)' }}>
                {t('compare.union.requiredExams', { exams: exams.join(', ') })}
              </div>
            )}
          </div>
        );
      },
    },
    {
      key: 'notes', label: t('compare.union.rows.staffNotes'),
      render: item => rv(item).staff_notes ? <div style={{ whiteSpace: 'pre-wrap' }}>{rv(item).staff_notes}</div> : null,
    },
  ];

  return (
    <CompareOverlay
      eyebrow={t('compare.union.eyebrow')}
      title={t('compare.union.title', { count: applicants.length })}
      onClose={onClose}
      onClear={applicants.length > 0 ? onClear : null}
      clearLabel={t('common.clearAll')}
      closeLabel={t('compare.closeAriaLabel')}
    >
      <CompareCards items={items} rows={rows} loading={loading} onRemove={onRemove} />
    </CompareOverlay>
  );
}
